import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerWageResource } from '@/types/resources';
import { api } from '@/utils/api';
import { renderCurrencySign } from '@/utils/helpers';

const Heading = styled(Text)`
  margin-bottom: 6px;
  text-transform: capitalize;
`;

const StyledText = styled(Text)`
  line-height: 22px;
`;

const Wages = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 12px;
  gap: 16px;
`;

const WageBullet = styled(Text)`
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 100px;
  padding: 8px 12px;
  padding-right: 8px;
  display: flex;
  align-items: center;
  span {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const RemoveButton = styled.button`
  width: 15px;
  height: 15px;
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.gray};
  border-radius: 50%;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const DateHolder = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const Dot = styled.div`
  margin: 0 4px;
`;

interface Props {
  type: 'hour' | 'day' | 'month' | 'job' | 'other';
  wages?: FreelancerWageResource[];
}
const FreelancerWageRates: FC<Props> = ({ type, wages }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const userCanMangeData = useHasPermission('manage freelancers');

  // The backend sends wages newest first.
  const wagesOfType = wages?.filter((wage) => wage.type === type);

  const renderWageType = () => {
    switch (type) {
      case 'hour':
        return 'Hour';
      case 'day':
        return 'Day';
      case 'month':
        return 'Month';
      case 'job':
        return 'Single Job';
      case 'other':
        return 'Other';
    }
  };

  const onWageRemove = async (wageId: number) => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure you want to do delete this wage?',
      );

      if (!confirmed) return;

      await api.delete(`/v1/payments/freelancers/department/wages/${wageId}`);

      await queryClient.invalidateQueries(['freelancers_details', { id }]);

      alerts.success('Wage has been successfully deleted');
    } catch (_) {
      alerts.error();
    }
  };

  return (
    <div>
      <Heading $type="smallHeadline" $color="gray">
        {renderWageType()} Wage Logs
      </Heading>
      {!!wagesOfType?.length ? (
        <Wages>
          {wagesOfType.map((wage) => (
            <WageBullet key={wage.id} $type="smallHeadline">
              <DateHolder>
                {DateTime.fromISO(wage.wage_changed_at).toFormat('dd/LL/yyyy')}
                <Dot>·</Dot>
              </DateHolder>
              <span>
                {type === 'other' ? wage.text_amount : wage.amount}
                {wage.currency && renderCurrencySign(wage.currency)}
              </span>
              {userCanMangeData && (
                <RemoveButton onClick={() => onWageRemove(wage.id)}>
                  <svg
                    width="7"
                    height="7"
                    viewBox="0 0 7 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M6.77917 1.28033C7.07206 0.987437 7.07206 0.512563 6.77917 0.21967C6.48628 -0.0732233 6.0114 -0.0732233 5.71851 0.21967L3.49942 2.43876L1.28033 0.21967C0.987437 -0.0732233 0.512563 -0.0732233 0.21967 0.21967C-0.0732233 0.512563 -0.0732233 0.987437 0.21967 1.28033L2.43876 3.49942L0.21967 5.71851C-0.0732233 6.0114 -0.0732233 6.48628 0.21967 6.77917C0.512563 7.07206 0.987437 7.07206 1.28033 6.77917L3.49942 4.56008L5.71851 6.77917C6.0114 7.07206 6.48628 7.07206 6.77917 6.77917C7.07206 6.48628 7.07206 6.0114 6.77917 5.71851L4.56008 3.49942L6.77917 1.28033Z"
                      fill="currentColor"
                    />
                  </svg>
                </RemoveButton>
              )}
            </WageBullet>
          ))}
        </Wages>
      ) : (
        <StyledText $type="captionSemibold">-</StyledText>
      )}
    </div>
  );
};

export default FreelancerWageRates;
